import { useMutation, useQuery } from "@apollo/client";
import {
    IActionPlan,
    IActionPlanComment,
    IActionPlanEntity,
    IActionPlanTableOperationFilters,
    IActionPlanTask,
} from "./ActionPlanEntityInterfaces";
import {
    getActionPlan,
    getActionPlanComments,
    getActionPlanCommentsCount,
    getActionPlanHistoricChangesLog,
    getActionPlanTasks,
    insertActionPlan,
    insertActionPlanComment,
    insertActionPlanTask,
    updateActionPlan,
    updateActionPlanComment,
    updateActionPlanMedia,
    updateActionPlanTask,
} from "api/queries/RCA/ActionPlanAPI";
import { useEffect, useMemo, useState } from "react";
import { IMyActionPlansFilters } from "pages/Corporate/RCA/RCADashboard/components/MyActionPlans/MyActionPlansInterfaces";
import { useActionPlanList } from "./useActionPlanList";
import { IRCAActionPlansFilterSelected } from "pages/Corporate/RCA/RCAActionPlans/components/RCAActionPlansFilters/RCAActionPlansFiltersInterfaces";
import { notSelected, RCA_STATUSES } from "constants/constants";
import { IHistoryLogBase } from "pages/Corporate/RCA/components/RCAHistoryLogsComponents";
import { COMMENTS_PER_PAGE_ACTION_PLAN_LIMIT } from "pages/Corporate/RCA/RCAActionPlans/components/RCAActionPlanDetail/components/ActionPlanComments/ActionPlanComments.view";

export const ActionPlanEntity: IActionPlanEntity = {
    InsertActionPlan: () => {
        const [createActionPlan, { error }] = useMutation(insertActionPlan);
        return { apolloMutationError: error, createActionPlan };
    },
    GetAllUserActionPlans: (
        tableOperationFilters: IActionPlanTableOperationFilters,
        userId: number,
        filters: IMyActionPlansFilters,
    ) => {
        const whereVariables = useMemo(
            () => ({
                _or: [{ submitted_by_user_id: { _eq: userId } }, { assignee_id: { _eq: userId } }],
                ...(!filters.showCompleted
                    ? {
                          _and: [
                              {
                                  status: {
                                      _and: [
                                          { status_id: { _neq: RCA_STATUSES.COMPLETED } },
                                          { status_id: { _neq: RCA_STATUSES.NEEDS_APPROVAL } },
                                      ],
                                  },
                              },
                          ],
                      }
                    : {
                          status: {
                              status_id: { _eq: RCA_STATUSES.COMPLETED },
                          },
                      }),
            }),
            [filters, userId],
        );

        const { items, totalCount, loading, apolloQueryError: error, refetch } = useActionPlanList(
            whereVariables,
            tableOperationFilters,
        );

        return { apolloQueryError: error, userActionPlans: items, loading, refetch, totalCount };
    },
    GetAllActionPlans: (
        tableOperationFilters: IActionPlanTableOperationFilters,
        filters: IRCAActionPlansFilterSelected,
    ) => {
        const whereVariables = useMemo(
            () => ({
                ...(filters?.meetingTypeIds &&
                    !filters.meetingTypeIds.includes(notSelected) &&
                    filters.totalMeetingTypes &&
                    filters.meetingTypeIds.length !== filters.totalMeetingTypes && {
                        _or: [
                            { meeting_type_id: { _in: filters.meetingTypeIds } },
                            {
                                meeting_type_id: { _is_null: true },
                                _or: [
                                    { rca: { meeting_type_id: { _in: filters.meetingTypeIds } } },
                                    { issue: { meeting_type_id: { _in: filters.meetingTypeIds } } },
                                ],
                            },
                        ],
                    }),
                ...(filters?.meetingTypeIds &&
                    filters.meetingTypeIds.includes(notSelected) && {
                        meeting_type_id: { _is_null: true },
                        _or: [
                            { rca: { meeting_type_id: { _is_null: true } } },
                            { issue: { meeting_type_id: { _is_null: true } } },
                        ],
                    }),
                assignee_id: {
                    _in: filters.ownerIds,
                },
                status: {
                    status_id: {
                        _in: filters.statusesIds,
                        ...(!filters.showCompleted
                            ? {
                                  _neq: RCA_STATUSES.COMPLETED,
                              }
                            : {
                                  _eq: RCA_STATUSES.COMPLETED,
                              }),
                    },
                },
                ...(filters.searchText && {
                    _or: [
                        ...(Number.isInteger(parseInt(filters.searchText))
                            ? [{ sequence_id: { _eq: parseInt(filters.searchText) } }]
                            : []),
                        ...(Number.isInteger(parseInt(filters.searchText))
                            ? [{ rca_id: { _eq: parseInt(filters.searchText) } }]
                            : []),
                        ...(Number.isInteger(parseInt(filters.searchText))
                            ? [{ issue_id: { _eq: parseInt(filters.searchText) } }]
                            : []),
                        { name: { _ilike: `%${filters.searchText}%` } },
                        { description: { _ilike: `%${filters.searchText}%` } },
                        { assignee: { first_name: { _ilike: `%${filters.searchText}%` } } },
                        { assignee: { last_name: { _ilike: `%${filters.searchText}%` } } },
                    ],
                }),
            }),
            [filters],
        );

        const { items, totalCount, loading, apolloQueryError: error, refetch } = useActionPlanList(
            whereVariables,
            tableOperationFilters,
        );

        return { apolloQueryError: error, actionPlans: items, loading, refetch, totalCount };
    },
    GetAllActionPlansForApproval: (tableOperationFilters: IActionPlanTableOperationFilters, userId?: number) => {
        const whereVariables = useMemo(
            () => ({
                ...(userId
                    ? {
                          _or: [
                              { created_by: { _eq: userId } },
                              { submitted_by_user_id: { _eq: userId } },
                              { assignee_id: { _eq: userId } },
                          ],
                      }
                    : {}),
                status: {
                    status_id: {
                        _eq: RCA_STATUSES.NEEDS_APPROVAL,
                    },
                },
            }),
            [userId],
        );

        const { items, totalCount, loading, apolloQueryError: error, refetch } = useActionPlanList(
            whereVariables,
            tableOperationFilters,
        );

        return { apolloQueryError: error, actionPlans: items, loading, refetch, totalCount };
    },
    GetActionPlanHistoricChangesLog: (id?: number) => {
        const [actionPlanLogs, setActionPlanLogs] = useState<IHistoryLogBase[]>([]);

        const { error, loading, refetch } = useQuery(getActionPlanHistoricChangesLog, {
            skip: !id,
            onCompleted: (data) => {
                if (data?.action_plan_history_log && Array.isArray(data.action_plan_history_log)) {
                    setActionPlanLogs(data.action_plan_history_log);
                }
            },
            variables: {
                id: id,
            },
        });

        return { apolloQueryError: error, actionPlanLogs, loading, refetch };
    },
    GetActionPlan: (actionPlanId: number) => {
        const { data, error, loading, refetch } = useQuery(getActionPlan, {
            fetchPolicy: "no-cache",
            variables: { action_plan_id: actionPlanId },
        });
        const [actionPlan, setActionPlan] = useState<IActionPlan>({} as IActionPlan);
        useEffect(() => {
            if (data?.action_plan && Array.isArray(data.action_plan) && data.action_plan[0]) {
                setActionPlan(data.action_plan[0]);
            }
        }, [data]);

        return { apolloQueryError: error, actionPlan, loading, refetch };
    },
    UpdateActionPlan: () => {
        const [updateActionPlanMutation, { error }] = useMutation(updateActionPlan);
        return { apolloMutationError: error, updateActionPlanMutation };
    },
    GetActionPlanTasks: (actionPlanId: number) => {
        const { data, error, loading, refetch } = useQuery(getActionPlanTasks, {
            fetchPolicy: "no-cache",
            variables: { actionPlanId: actionPlanId },
            skip: !actionPlanId,
        });

        const [actionPlanTasks, setActionPlanTasks] = useState<IActionPlanTask[]>([]);

        useEffect(() => {
            if (data?.action_plan_task && Array.isArray(data.action_plan_task)) {
                setActionPlanTasks(data.action_plan_task);
            }
        }, [data]);

        return { apolloQueryError: error, actionPlanTasks, loading, refetch };
    },
    InsertActionPlanTask: () => {
        const [insertActionPlanTaskMutation, { error }] = useMutation(insertActionPlanTask);
        return { apolloMutationError: error, insertActionPlanTaskMutation };
    },
    UpdateActionPlanTask() {
        const [updateActionPlanTaskMutation, { error }] = useMutation(updateActionPlanTask);
        return { apolloMutationError: error, updateActionPlanTaskMutation };
    },
    UpdateActionPlanMedia: () => {
        const [updateActionPlanMediaMutation, { error }] = useMutation(updateActionPlanMedia);
        return { apolloMutationError: error, updateActionPlanMediaMutation };
    },
    GetActionPlanComments: (actionPlanId: number) => {
        const [totalCount, setTotalCount] = useState<number>(0);
        const [actionPlanComments, setActionPlanComments] = useState<IActionPlanComment[]>([]);

        const { refetch: refetchTotalCount } = useQuery(getActionPlanCommentsCount, {
            fetchPolicy: "no-cache",
            skip: !actionPlanId,
            onCompleted: (data) => {
                if (data?.action_plan_comment_aggregate) {
                    const totalCount: number = data?.action_plan_comment_aggregate?.aggregate?.count || 0;
                    setTotalCount(totalCount);
                    if (totalCount < 1) {
                        setActionPlanComments([]);
                    }
                }
            },
            variables: {
                actionPlanId: actionPlanId,
            },
        });

        const { error, loading, refetch } = useQuery(getActionPlanComments, {
            fetchPolicy: "no-cache",
            skip: !actionPlanId || !totalCount,
            onCompleted: (data) => {
                if (data?.action_plan_comment && Array.isArray(data.action_plan_comment)) {
                    setActionPlanComments(data.action_plan_comment);
                }
            },
            variables: {
                actionPlanId: actionPlanId,
                limit: COMMENTS_PER_PAGE_ACTION_PLAN_LIMIT,
                offset: 0,
                order_by: {
                    created_at: "desc",
                },
            },
        });

        return { apolloQueryError: error, actionPlanComments, loading, refetch, refetchTotalCount, totalCount };
    },
    InsertActionPlanComment: () => {
        const [createActionPlanComment, { error }] = useMutation(insertActionPlanComment);
        return { apolloMutationError: error, createActionPlanComment };
    },
    UpdateActionPlanComment: () => {
        const [updateActionPlanCommentMutation, { error }] = useMutation(updateActionPlanComment);
        return { apolloMutationError: error, updateActionPlanComment: updateActionPlanCommentMutation };
    },
};
